import {
  GlobalOutlined,
  LogoutOutlined,
  OrderedListOutlined,
  ShareAltOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { history, setLocale, useModel, useRequest } from '@umijs/max';
import { Avatar, Button, Popover, Segmented, message } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { loginModal } from '@/components/LoginModal';
import { paymentModal } from '@/components/PaymentModal';
import {
  DASHBOARD_DATA_CACHE_KEY,
  extractDashboardLedgerBalance,
  fetchDashboardData,
} from '@/services/dashboard';
import { outLogin } from '@/services/auth';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';

const LANGUAGE_LABELS = {
  'zh-CN': '中',
  'en-US': 'EN',
} as const;

const useStyles = createStyles(({ token, css }) => {
  const borderColor = `color-mix(in srgb, ${token.colorBorderSecondary} 88%, ${token.colorBgContainer})`;
  const footerBackground = 'transparent';
  const hoverBackground = `color-mix(in srgb, ${token.colorFillSecondary} 74%, ${token.colorBgContainer})`;
  const subtleBackground = `color-mix(in srgb, ${token.colorBgContainer} 84%, ${token.colorFillQuaternary})`;
  const focusRing = `0 0 0 2px color-mix(in srgb, ${token.colorPrimaryBg} 75%, transparent)`;

  return {
    root: css`
      width: 100%;
      margin: 0;
      padding: 0;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 84%, transparent);
      background: ${footerBackground};
      overflow: hidden;
    `,
    collapsedRoot: css`
      padding: 10px 0 12px;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 84%, transparent);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: ${footerBackground};
    `,
    collapsedUserBadge: css`
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      background: ${subtleBackground};
      box-shadow: inset 0 0 0 1px color-mix(in srgb, ${borderColor} 84%, transparent);
    `,
    footerPanel: css`
      padding: 14px 10px 0;
      background:
        linear-gradient(
          180deg,
          ${token.colorBgContainer} 0%,
          color-mix(in srgb, ${subtleBackground} 92%, ${token.colorBgContainer}) 100%
        );
      box-shadow: 0 10px 30px -28px rgba(15, 23, 42, 0.18);
    `,
    loginPromptRow: css`
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      width: 100%;
      min-width: 0;
      padding: 0;
      box-sizing: border-box;
    `,
    accountHeaderRow: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      width: 100%;
      min-width: 0;
      padding: 0;
      box-sizing: border-box;
    `,
    loginPromptMeta: css`
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 4px;
    `,
    loginPromptTitle: css`
      font-size: 14px;
      line-height: 20px;
      font-weight: 600;
      color: ${token.colorTextHeading};
    `,
    loginPromptDesc: css`
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextSecondary};
    `,
    footerSections: css`
      display: flex;
      flex-direction: column;
      gap: 0;
      margin-top: 12px;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 90%, transparent);
    `,
    footerActionButton: css`
      height: 36px;
      padding-inline: 16px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 500;
      box-shadow: none;
      flex-shrink: 0;
    `,
    accountHeaderActionButton: css`
      height: 32px;
      padding-inline: 14px;
      border: 1px solid color-mix(
        in srgb,
        ${token.colorBorderSecondary} 84%,
        ${token.colorBgContainer}
      );
      border-radius: 999px;
      background: color-mix(
        in srgb,
        ${token.colorBgContainer} 72%,
        ${token.colorFillQuaternary}
      );
      color: ${token.colorTextSecondary};
      font-size: 12px;
      font-weight: 500;
      line-height: 1;
      box-shadow: none;
      flex-shrink: 0;
      transition:
        background-color 0.2s ease,
        border-color 0.2s ease,
        color 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        color: ${token.colorText};
        border-color: color-mix(
          in srgb,
          ${token.colorPrimaryBorder} 58%,
          ${token.colorBorderSecondary}
        );
        background: color-mix(in srgb, ${hoverBackground} 78%, ${token.colorBgContainer});
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    accountCard: css`
      width: 100%;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 12px;
      border-radius: 18px;
      border: 1px solid color-mix(in srgb, ${borderColor} 92%, transparent);
      background:
        linear-gradient(
          180deg,
          ${token.colorBgContainer} 0%,
          color-mix(in srgb, ${subtleBackground} 92%, ${token.colorBgContainer}) 100%
        );
      box-sizing: border-box;
      box-shadow: 0 10px 30px -28px rgba(15, 23, 42, 0.18);
    `,
    accountLead: css`
      display: flex;
      align-items: center;
      gap: 9px;
      min-width: 0;
      flex: 1;
    `,
    accountMeta: css`
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      text-align: left;
    `,
    accountName: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 14px;
      line-height: 19px;
      font-weight: 500;
      color: ${token.colorTextHeading};
    `,
    accountSubtitle: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
      line-height: 16px;
      color: ${token.colorTextSecondary};
    `,
    expandedAvatar: css`
      background: ${token.colorPrimary};
      flex-shrink: 0;
    `,
    collapsedButton: css`
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: ${token.colorTextSecondary};
      cursor: pointer;
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    collapsedAvatar: css`
      background: ${token.colorPrimary};
    `,
    collapsedPinnedActionButton: css`
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 12px;
      background: ${subtleBackground};
      color: ${token.colorText};
      cursor: pointer;
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    localePopoverBody: css`
      width: 240px;
      padding: 6px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
    sectionDivider: css`
      width: 100%;
      height: 1px;
      background: color-mix(in srgb, ${borderColor} 90%, transparent);
    `,
    balancePanel: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    balanceMeta: css`
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    balanceLabel: css`
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextSecondary};
    `,
    balanceValue: css`
      font-size: 18px;
      line-height: 24px;
      font-weight: 600;
      color: ${token.colorTextHeading};
      letter-spacing: -0.02em;
    `,
    rechargeButton: css`
      height: 32px;
      padding-inline: 12px;
      border-radius: 999px;
      box-shadow: none;
      font-size: 12px;
      font-weight: 500;
    `,
    sectionTitle: css`
      padding: 2px 8px 0;
      font-size: 11px;
      line-height: 18px;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: ${token.colorTextTertiary};
    `,
    settingBlock: css`
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    settingRow: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    `,
    settingBlockEmbedded: css`
      padding: 8px 6px;
      border-radius: 0;
      background: transparent;
    `,
    settingRowEmbedded: css`
      gap: 16px;
    `,
    settingLead: css`
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      text-align: left;
      line-height: 20px;
      color: ${token.colorText};
    `,
    settingIcon: css`
      width: 16px;
      height: 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      line-height: 1;
      color: ${token.colorTextSecondary};
      font-size: 14px;
    `,
    embeddedSegmented: css`
      padding: 2px;
      border-radius: 12px;
      background: color-mix(in srgb, ${token.colorFillSecondary} 70%, ${token.colorBgContainer});
      box-shadow: inset 0 0 0 1px color-mix(in srgb, ${token.colorBorderSecondary} 75%, transparent);

      .ant-segmented-item {
        min-width: 38px;
        border-radius: 10px;
      }

      .ant-segmented-item-label {
        padding-inline: 10px;
        font-size: 12px;
      }
    `,
    actionList: css`
      display: flex;
      flex-direction: column;
      gap: 4px;
    `,
    actionPanel: css`
      padding: 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    actionButton: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 40px;
      padding: 0 10px;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: ${token.colorText};
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }
    `,
    actionLead: css`
      display: inline-flex;
      align-items: center;
      text-align: left;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      line-height: 20px;
    `,
    pinnedActionLead: css`
      display: inline-flex;
      align-items: center;
      text-align: left;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      line-height: 20px;
    `,
    pinnedActionLabel: css`
      display: block;
      min-width: 0;
      line-height: 20px;
    `,
    actionMeta: css`
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextTertiary};
    `,
    loggedInPanel: css`
      display: flex;
      flex-direction: column;
      gap: 12px;
      padding: 12px;
    `,
    pinnedActionSection: css`
      border-top: 1px solid color-mix(in srgb, ${borderColor} 90%, transparent);
    `,
    pinnedActionButton: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 40px;
      padding: 8px 6px;
      border: none;
      border-radius: 5px;
      background: transparent;
      color: ${token.colorText};
      cursor: pointer;
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:focus-visible {
        outline: none;
        box-shadow: inset ${focusRing};
      }
    `,
  };
});

type FooterActionItem = {
  key: string;
  label: string;
  icon: React.ElementType;
  onClick: () => void | Promise<void>;
};

const SiderFooter: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { styles } = useStyles();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [localePopoverOpen, setLocalePopoverOpen] = React.useState(false);

  const isLoggedIn = !!initialState?.currentUser;
  const user = initialState?.currentUser?.user;
  const affCodeInfo = initialState?.affCodeInfo;
  const userId = user?.id;
  const currentLocale = getAppLocale();

  const { data: dashboardData } = useRequest(
    () => fetchDashboardData({ skipErrorHandler: true }),
    {
      ready: Boolean(userId),
      cacheKey: DASHBOARD_DATA_CACHE_KEY,
      staleTime: 5_000,
      refreshOnWindowFocus: false,
    },
  );
  const balance = React.useMemo(() => {
    if (!userId) {
      return 0;
    }
    return extractDashboardLedgerBalance(dashboardData);
  }, [dashboardData, userId]);

  const balanceText = React.useMemo(() => {
    const amount = Number(balance || 0);
    if (!Number.isFinite(amount)) {
      return '0';
    }

    return new Intl.NumberFormat(currentLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, [balance, currentLocale]);

  const userName =
    user?.name || t('components.siderFooter.notLoggedIn', 'Not signed in');
  const accountSubtitle =
    user?.account ||
    t('components.siderFooter.personalAccount', 'Personal account');

  const handleRecharge = (defaultTab: 'plans' | 'recharge' = 'recharge') => {
    paymentModal.show({ defaultTab });
  };

  const loginOut = async () => {
    localStorage.removeItem('token');
    await outLogin();
    message.success(
      t('components.siderFooter.logoutSuccess', 'Logged out successfully'),
    );
    history.replace('/home');
  };

  const handleLogout = async () => {
    flushSync(() => {
      setInitialState((state) => ({ ...state, currentUser: undefined }));
    });

    await loginOut();
  };

  const handleLocaleChange = (value: string | number) => {
    const nextLocale = String(value);
    if (nextLocale !== currentLocale) {
      setLocalePopoverOpen(false);
      setLocale(nextLocale, false);
    }
  };

  const handleCopyInviteLink = () => {
    if (!affCodeInfo?.affLink) return;
    navigator.clipboard.writeText(affCodeInfo.affLink).then(
      () =>
        message.success(
          t(
            'components.siderFooter.inviteLinkCopied',
            'Invite link copied to clipboard',
          ),
        ),
      () =>
        message.error(
          t(
            'components.siderFooter.copyFailed',
            'Copy failed, please copy it manually',
          ),
        ),
    );
  };

  const handleOpenTransactionHistory = () => {
    history.push('/payment/orders');
  };

  /**
   * 账户动作统一在这里组装。
   * 这样展开态和折叠态都消费同一份配置，避免把某个入口只留在旧弹层里。
   */
  const accountActions: FooterActionItem[] = [];
  if (affCodeInfo?.affLink) {
    accountActions.push({
      key: 'invite-link',
      label: t('components.siderFooter.inviteLink', 'Invite Link'),
      icon: ShareAltOutlined,
      onClick: handleCopyInviteLink,
    });
  }
  accountActions.push(
    {
      key: 'transaction-history',
      label: t(
        'components.siderFooter.transactionHistory',
        'Transaction History',
      ),
      icon: OrderedListOutlined,
      onClick: handleOpenTransactionHistory,
    },
    {
      key: 'logout',
      label: t('components.siderFooter.logout', 'Logout'),
      icon: LogoutOutlined,
      onClick: handleLogout,
    },
  );

  /**
   * 登录态展开区已经把“退出登录”提升成右上角主按钮，
   * 所以下方列表只保留需要继续平铺展示的动作，避免用户在同一块面板里看到重复入口。
   */
  const expandedListActions = accountActions.filter(
    (action) => action.key !== 'logout',
  );

  /**
   * 语言偏好不能依赖登录态。
   * 游客第一次进入站点时，也需要在不打开登录弹窗的前提下把界面切到可读语言；
   * 但“偏好设置”标题只在登录态账户面板里有意义，否则会让游客误以为还有更多配置项可用。
   */
  const renderLocalePreferenceBlock = ({
    showTitle = true,
    embedded = false,
  }: {
    showTitle?: boolean;
    embedded?: boolean;
  } = {}) => (
    <>
      {showTitle && (
        <div className={styles.sectionTitle}>
          {t('components.siderFooter.preferences', 'Preferences')}
        </div>
      )}
      <div
        className={[
          styles.settingBlock,
          embedded ? styles.settingBlockEmbedded : '',
        ].join(' ')}
      >
        <div
          className={[
            styles.settingRow,
            embedded ? styles.settingRowEmbedded : '',
          ].join(' ')}
        >
          <span className={styles.settingLead}>
            <GlobalOutlined className={styles.settingIcon} />
            <span>{t('components.siderFooter.language', 'Language')}</span>
          </span>
          <Segmented
            className={embedded ? styles.embeddedSegmented : undefined}
            value={currentLocale}
            onChange={handleLocaleChange}
            options={Object.entries(LANGUAGE_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </div>
      </div>
    </>
  );

  /**
   * “订阅与充值”是侧栏底部的固定主操作，不应依赖登录态之外的临时展开容器。
   * 登录态里它和用户入口需要保持同一视觉节奏，避免像两个不相关的模块；
   * 展开态使用带分割线的列表项，而不是独立胶囊按钮，这样能更自然地挂在设置区下面。
   */
  const renderPinnedSubscriptionAction = ({
    compact = false,
  }: {
    compact?: boolean;
  } = {}) => {
    const actionLabel = t(
      'components.siderFooter.subscriptionAndRecharge',
      'Subscription & Recharge',
    );

    if (compact) {
      return (
        <button
          type="button"
          className={styles.collapsedPinnedActionButton}
          onClick={() => handleRecharge('plans')}
          title={actionLabel}
          aria-label={actionLabel}
        >
          <WalletOutlined style={{ fontSize: 16 }} />
        </button>
      );
    }

    return (
      <button
        type="button"
        className={styles.pinnedActionButton}
        onClick={() => handleRecharge('plans')}
      >
        <span className={styles.pinnedActionLead}>
          <WalletOutlined className={styles.settingIcon} />
          <span className={styles.pinnedActionLabel}>{actionLabel}</span>
        </span>
        <span className={styles.actionMeta}>
          {t('components.siderFooter.balanceValue', '{amount}', {
            amount: balanceText,
          })}
        </span>
      </button>
    );
  };

  /**
   * 登录态展开区改成和游客态相同的行式列表后，
   * 其它账户动作也需要复用同一套行高、分割线与点击反馈，避免 footer 里出现两种风格。
   */
  const renderExpandedActionRow = (action: FooterActionItem) => {
    const Icon = action.icon;
    return (
      <button
        key={action.key}
        type="button"
        className={styles.pinnedActionButton}
        onClick={action.onClick}
      >
        <span className={styles.pinnedActionLead}>
          <Icon className={styles.settingIcon} />
          <span className={styles.pinnedActionLabel}>{action.label}</span>
        </span>
      </button>
    );
  };

  const localePopoverContent = (
    <div className={styles.localePopoverBody}>
      {renderLocalePreferenceBlock({ showTitle: false })}
    </div>
  );

  const renderCollapsedActionButton = (action: FooterActionItem) => {
    const Icon = action.icon;
    return (
      <button
        key={action.key}
        type="button"
        className={styles.collapsedButton}
        onClick={action.onClick}
        title={action.label}
        aria-label={action.label}
      >
        <Icon style={{ fontSize: 16 }} />
      </button>
    );
  };

  if (collapsed) {
    return (
      <div className={styles.collapsedRoot}>
        {isLoggedIn ? (
          <>
            <div className={styles.collapsedUserBadge} title={userName}>
              <Avatar
                size={22}
                icon={<UserOutlined />}
                src={user?.avatar}
                className={styles.collapsedAvatar}
              />
            </div>
            <button
              type="button"
              className={styles.collapsedButton}
              onClick={() => handleRecharge('recharge')}
              title={t('components.siderFooter.credits', 'Credits')}
              aria-label={t('components.siderFooter.credits', 'Credits')}
            >
              <ThunderboltOutlined style={{ fontSize: 16 }} />
            </button>
            {renderPinnedSubscriptionAction({ compact: true })}
            <Popover
              trigger="click"
              placement="topRight"
              open={localePopoverOpen}
              onOpenChange={setLocalePopoverOpen}
              content={localePopoverContent}
              styles={{ body: { padding: 0 } }}
            >
              <button
                type="button"
                className={styles.collapsedButton}
                title={t('components.siderFooter.language', 'Language')}
                aria-label={t('components.siderFooter.language', 'Language')}
              >
                <GlobalOutlined style={{ fontSize: 16 }} />
              </button>
            </Popover>
            {/* 折叠态无法展示完整资料卡，所以把原来在用户弹层里的动作拆成独立图标。 */}
            {accountActions.map(renderCollapsedActionButton)}
          </>
        ) : (
          <>
            {renderPinnedSubscriptionAction({ compact: true })}
            <Popover
              trigger="click"
              placement="topRight"
              open={localePopoverOpen}
              onOpenChange={setLocalePopoverOpen}
              content={localePopoverContent}
              styles={{ body: { padding: 0 } }}
            >
              <button
                type="button"
                className={styles.collapsedButton}
                title={t('components.siderFooter.language', 'Language')}
                aria-label={t('components.siderFooter.language', 'Language')}
              >
                <GlobalOutlined style={{ fontSize: 16 }} />
              </button>
            </Popover>
            <button
              type="button"
              className={styles.collapsedButton}
              onClick={() => loginModal.show()}
              title={t('components.siderFooter.login', 'Login')}
              aria-label={t('components.siderFooter.login', 'Login')}
            >
              <UserOutlined style={{ fontSize: 16 }} />
            </button>
          </>
        )}
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className={styles.root}>
        <div className={styles.footerPanel}>
          <div className={styles.loginPromptRow}>
            <div className={styles.loginPromptMeta}>
              <div className={styles.loginPromptTitle}>
                {t(
                  'components.siderFooter.loginPromptTitle',
                  'Get responses tailored to you',
                )}
              </div>
              <div className={styles.loginPromptDesc}>
                {t(
                  'components.siderFooter.loginPromptDesc',
                  'Log in to sync chats, access credits, and keep your preferences consistent.',
                )}
              </div>
            </div>
            <Button
              className={styles.footerActionButton}
              onClick={() => loginModal.show()}
            >
              {t('components.siderFooter.login', 'Login')}
            </Button>
          </div>
          <div className={styles.footerSections}>
            {renderLocalePreferenceBlock({ showTitle: false, embedded: true })}
            <div className={styles.pinnedActionSection}>
              {renderPinnedSubscriptionAction()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.footerPanel}>
        <div className={styles.accountHeaderRow}>
          <span className={styles.accountLead}>
            <Avatar
              size={30}
              icon={<UserOutlined />}
              src={user?.avatar}
              className={styles.expandedAvatar}
            />
            <span className={styles.accountMeta}>
              <span className={styles.accountName}>{userName}</span>
              <span className={styles.accountSubtitle}>{accountSubtitle}</span>
            </span>
          </span>
          {/**
           * 登录态右上角保留“退出登录”捷径，但这里降为轻量次级动作，
           * 避免它像主 CTA 一样抢占头像和账户信息的视觉重心。
           */}
          <Button
            className={styles.accountHeaderActionButton}
            onClick={handleLogout}
          >
            {t('components.siderFooter.logout', 'Logout')}
          </Button>
        </div>

        <div className={styles.footerSections}>
          {renderLocalePreferenceBlock({ showTitle: false, embedded: true })}
          <div className={styles.pinnedActionSection}>
            {renderPinnedSubscriptionAction()}
          </div>
          {expandedListActions.map((action) => (
            <div key={action.key} className={styles.pinnedActionSection}>
              {renderExpandedActionRow(action)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SiderFooter;
